import { numberParam, optionalNumberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import type { NormalizedRect } from '../types/geometry.js';
import { clamp, roundTo } from '../types/geometry.js';
import type { FaceGeometryMetrics, RoiRegionDetail } from '../types/metrics.js';
import type { OfficialBounds, PolicySpec } from '../types/profile.js';
import { computeRoiSharpness } from '../quality/sharpness.js';
import {
  judgeBounds,
  judgeRange,
  judgeRule,
  missingRequirementReason,
  notChecked,
  plainResult,
} from './support.js';

/**
 * Sharpness / blur check.
 *
 * Blur is the one defect a crop can never repair, so a failure always means
 * "retake". The verdict is based on the resolution-normalized ROI score, not on
 * the whole-frame Laplacian variance: a large face in a small analysis buffer
 * and a small face in a large one are now judged on the same scale.
 *
 * The profile still owns the thresholds. `params.minSharpnessScore` is the main
 * bound (default 0.35), `params.warnSharpnessScore` the soft band (default
 * 0.2). `params.minLaplacianVariance` / `minTenengrad` are kept only as the
 * fallback for frames where no ROI could be measured.
 */

export interface SharpnessRegion {
  id: RoiRegionDetail['id'];
  rect: NormalizedRect;
}

/** Defaults live in code only so a profile without score params still works. */
export const DEFAULT_MIN_SHARPNESS_SCORE = 0.35;
export const DEFAULT_WARN_SHARPNESS_SCORE = 0.2;
/** Below this confidence a failure is never reported. */
export const FAIL_CONFIDENCE_FLOOR = 0.6;
/** Below this confidence the verdict is always soft. */
export const LOW_CONFIDENCE_FLOOR = 0.35;

const clampRect = (rect: NormalizedRect): NormalizedRect => {
  const x = clamp(rect.x, 0, 1);
  const y = clamp(rect.y, 0, 1);
  return {
    x,
    y,
    width: clamp(rect.width, 0, 1 - x),
    height: clamp(rect.height, 0, 1 - y),
  };
};

/**
 * Turns face geometry into the ROIs the sharpness algorithm measures.
 *
 *  - face  — the face box shrunk slightly so background does not dominate;
 *  - eyes  — a thin band centred on the eye line, the most reliable signal;
 *  - nose  — a small box around the nose tip;
 *  - mouth — a small box around the mouth centre.
 *
 * Without geometry the whole frame is used (low confidence is reported by the
 * algorithm itself).
 */
export const sharpnessRegions = (geometry: FaceGeometryMetrics | undefined): SharpnessRegion[] => {
  if (!geometry) {
    return [{ id: 'face', rect: { x: 0, y: 0, width: 1, height: 1 } }];
  }

  const face = geometry.faceBox;
  const head = geometry.headBox;
  const inset = 0.12;
  const faceRect = clampRect({
    x: face.x + face.width * inset,
    y: face.y + face.height * inset,
    width: face.width * (1 - 2 * inset),
    height: face.height * (1 - 2 * inset),
  });

  // The eye band: ~12 % of the image height, centered on the eye line, no wider
  // than the head (we never want the ears/background to dominate).
  const eyeBandHeight = 0.12;
  const eyeWidth = Math.max(0.04, Math.min(head.width, face.width * 1.2));
  const eyeCenterX = head.x + head.width / 2;
  const eyesRect = clampRect({
    x: eyeCenterX - eyeWidth / 2,
    y: geometry.eyeLineFromTop - eyeBandHeight / 2,
    width: eyeWidth,
    height: eyeBandHeight,
  });

  const noseSide = Math.max(0.05, face.width * 0.3);
  const noseRect = clampRect({
    x: geometry.noseTip.x - noseSide / 2,
    y: geometry.noseTip.y - noseSide / 2,
    width: noseSide,
    height: noseSide,
  });

  const mouthSide = Math.max(0.06, face.width * 0.36);
  const mouthRect = clampRect({
    x: geometry.mouthCenter.x - mouthSide / 2,
    y: geometry.mouthCenter.y - mouthSide / 2,
    width: mouthSide,
    height: mouthSide,
  });

  return [
    { id: 'face', rect: faceRect },
    { id: 'eyes', rect: eyesRect },
    { id: 'nose', rect: noseRect },
    { id: 'mouth', rect: mouthRect },
  ];
};

interface ScoreThresholds {
  min: number;
  warn: number;
}

/**
 * Resolves the score thresholds from the profile.
 *
 * A profile may express the bound as `params.minSharpnessScore`, or as a
 * `policy.min` already on the 0..1 score scale. A legacy policy bound on the
 * Laplacian scale (e.g. 110) is deliberately ignored here — it belongs to the
 * fallback path.
 */
const resolveThresholds = (params: Record<string, unknown>, policy?: PolicySpec): ScoreThresholds => {
  const explicitMin = optionalNumberParam(params, 'minSharpnessScore');
  const policyMin =
    policy?.min !== undefined && policy.min > 0 && policy.min <= 1 ? policy.min : undefined;
  const min = explicitMin ?? policyMin ?? DEFAULT_MIN_SHARPNESS_SCORE;
  const explicitWarn = optionalNumberParam(params, 'warnSharpnessScore');
  const warn = explicitWarn ?? Math.min(DEFAULT_WARN_SHARPNESS_SCORE, min * 0.75);
  return {
    min: clamp(min, 0, 1),
    warn: clamp(warn, 0, Math.max(0, min)),
  };
};

const rank = { pass: 0, uncertain: 1, warning: 2, fail: 3, not_checked: 4 } as const;

const worst = (a: keyof typeof rank, b: keyof typeof rank): keyof typeof rank =>
  rank[a] >= rank[b] ? a : b;

/**
 * Three-band verdict for the 0..1 score.
 *
 * The two bounds are judged with the shared `judgeBounds` helper so tolerance
 * and confidence behave exactly like every other check; the band below the soft
 * bound additionally refuses to fail on low confidence.
 */
const judgeScore = (
  score: number,
  confidence: number,
  thresholds: ScoreThresholds,
): { status: CheckResult['status']; reason: string } => {
  const passVerdict = judgeBounds({
    value: score,
    official: { min: thresholds.min },
    effective: { min: thresholds.min },
    measurement: { tolerance: 0 },
  });
  if (passVerdict.status === 'pass') return { status: 'pass', reason: 'ok' };

  const warnVerdict = judgeBounds({
    value: score,
    official: { min: thresholds.warn },
    effective: { min: thresholds.warn },
    measurement: { tolerance: 0 },
  });
  const inSoftBand = warnVerdict.status === 'pass';

  // Low confidence never produces a failure — only a soft verdict.
  if (confidence < LOW_CONFIDENCE_FLOOR) {
    return inSoftBand
      ? { status: 'warning', reason: 'soft_band' }
      : { status: 'uncertain', reason: 'low_confidence' };
  }
  if (inSoftBand) return { status: 'warning', reason: 'soft_band' };
  if (confidence < FAIL_CONFIDENCE_FLOOR) return { status: 'uncertain', reason: 'low_confidence' };
  return { status: 'fail', reason: 'below_min' };
};

export const sharpnessCheck: ValidationCheck = {
  id: 'sharpness',
  title: 'Резкость',
  description:
    'Оценивает размытие по нормализованной детализации областей лица (глаза, лицо, нос, рот).',
  kind: 'quality',
  requires: ['quality', 'pixels'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['quality', 'pixels']);
    if (missing) return notChecked(ctx, sharpnessCheck, missing, ctx.rule.severity);
    const quality = ctx.quality;
    const pixels = ctx.pixels;
    if (!quality || !pixels) {
      return notChecked(ctx, sharpnessCheck, 'Пиксельные метрики недоступны.', ctx.rule.severity);
    }

    const { bounds } = judgeRule(ctx.rule, quality.sharpness.laplacianVariance, {
      confidence: 0.75,
    });
    const thresholds = resolveThresholds(ctx.rule.params, bounds.policy);
    const regions = sharpnessRegions(ctx.geometry);
    const roi = computeRoiSharpness({
      buffer: pixels,
      regions,
      noiseSigma: quality.noise.sigma,
    });
    const hasValidRoi = roi.regions.some((region) => region.sampleSize.width > 0);

    /**
     * Without face geometry the algorithm still measures the whole frame, but
     * it cannot know where the face is — so the verdict stays soft.
     */
    const notes = ctx.geometry ? roi.notes : [...roi.notes, 'no-geometry'];
    const confidence = ctx.geometry ? roi.confidence : Math.min(roi.confidence, 0.5);

    // ------------------------------------------------------- fallback path
    if (!hasValidRoi) {
      const minLaplacian = numberParam(ctx.rule.params, 'minLaplacianVariance', 90);
      const minTenengrad = numberParam(ctx.rule.params, 'minTenengrad', 60);
      const { laplacianVariance, tenengrad } = quality.sharpness;
      const laplacianVerdict = judgeRange({
        value: laplacianVariance,
        min: minLaplacian,
        warningMargin: 0.2,
      });
      const tenengradVerdict = judgeRange({
        value: tenengrad,
        min: minTenengrad,
        warningMargin: 0.2,
      });
      const status = worst(laplacianVerdict.status, tenengradVerdict.status) as CheckResult['status'];

      return plainResult({
        id: sharpnessCheck.id,
        title: sharpnessCheck.title,
        status,
        severity: ctx.rule.severity,
        value: roundTo(laplacianVariance, 1),
        min: minLaplacian,
        message:
          status === 'pass'
            ? 'Фотография достаточно резкая: черты лица не размыты.'
            : '⚠ Возможно недостаточная резкость: области лица измерить не удалось. Это приблизительная оценка.',
        requirement: 'Изображение должно быть резким, без смаза и расфокуса.',
        fixability: 'retake',
        confidence: 0.4,
        details: {
          mode: 'fallback',
          laplacianVariance: roundTo(laplacianVariance, 2),
          tenengrad: roundTo(tenengrad, 2),
          highFrequencyRatio: roundTo(quality.sharpness.highFrequencyRatio, 4),
          minLaplacianVariance: minLaplacian,
          minTenengrad,
          sampledAt: quality.sampledAt,
        },
      });
    }

    // ----------------------------------------------------------- score path
    const verdict = judgeScore(roi.score, confidence, thresholds);
    const estimate = 'Это оценка по областям лица (глаза, лицо, нос, рот), а не измерение.';

    const message = (() => {
      if (verdict.status === 'pass') {
        return 'Фотография достаточно резкая: черты лица не размыты.';
      }
      if (verdict.status === 'fail') {
        return `Фотография выглядит размытой: детализация областей лица ниже допустимой (оценка ${roi.score.toFixed(2)} при пороге ${thresholds.min.toFixed(2)}).`;
      }
      if (verdict.status === 'warning') {
        return `⚠ Возможно недостаточная резкость: детализация ${roi.score.toFixed(2)} близка к нижней границе ${thresholds.min.toFixed(2)}. ${estimate}`;
      }
      return `⚠ Возможно недостаточная резкость, но уверенно оценить не удалось (детализация ${roi.score.toFixed(2)}, уверенность ${confidence.toFixed(2)}). ${estimate}`;
    })();


    const geometry = roi.regions
      .filter((region) => region.sampleSize.width > 0)
      .map((region) => {
        const source = regions.find((item) => item.id === region.id);
        const rect = source?.rect ?? { x: 0, y: 0, width: 1, height: 1 };
        return {
          type: 'rect' as const,
          coordinates: [
            roundTo(rect.x, 4),
            roundTo(rect.y, 4),
            roundTo(rect.width, 4),
            roundTo(rect.height, 4),
          ] as [number, number, number, number],
        };
      });

    /**
     * Only surface a policy bound when it is expressed on the score scale.
     *
     * The shipped profiles still carry a legacy Laplacian policy (`min: 110`),
     * whose unit has nothing to do with the 0..1 ROI score. If it were treated
     * as a score bound the engine's central policy inference would turn every
     * sharp photo into a violation — the exact false positive this fix targets.
     * `policyApplies: false` tells the engine to skip that inference.
     */
    const scorePolicy: PolicySpec | undefined =
      bounds.policy &&
      ((bounds.policy.min !== undefined && bounds.policy.min <= 1) ||
        (bounds.policy.max !== undefined && bounds.policy.max <= 1))
        ? bounds.policy
        : undefined;
    const official: OfficialBounds = bounds.official;

    /**
     * Transitional bridge for profiles whose policy bound is still on the
     * legacy Laplacian scale: a *genuine* blur failure should still escalate to
     * the overall verdict through the profile's own `onViolation: 'error'`
     * declaration, exactly as it did before. Pass/warning photos never do.
     * Once a profile moves its policy to the 0..1 score scale this branch turns
     * itself off.
     */
    const legacyFailEscalation =
      scorePolicy === undefined && verdict.status === 'fail' && bounds.policy !== undefined;

    const details: Record<string, unknown> = {
      mode: 'roi',
      sharpnessScore: roundTo(roi.score, 4),
      confidence: roundTo(confidence, 2),
      notes,
      normalizedScale: roundTo(roi.normalizedScale, 3),
      minSharpnessScore: thresholds.min,
      warnSharpnessScore: thresholds.warn,
      policyApplies: scorePolicy !== undefined,
      verdictReason: verdict.reason,
      regions: roi.regions.map((region) => ({
        id: region.id,
        detail: roundTo(region.detail, 4),
        laplacianVariance: roundTo(region.laplacianVariance, 2),
        edgeDensity: roundTo(region.edgeDensity, 4),
        localContrast: roundTo(region.localContrast, 2),
        sampleSize: region.sampleSize,
      })),
      // Legacy numbers stay in the report for comparison and threshold tuning.
      laplacianVariance: roundTo(quality.sharpness.laplacianVariance, 2),
      tenengrad: roundTo(quality.sharpness.tenengrad, 2),
      highFrequencyRatio: roundTo(quality.sharpness.highFrequencyRatio, 4),
      minLaplacianVariance: numberParam(ctx.rule.params, 'minLaplacianVariance', 90),
      minTenengrad: numberParam(ctx.rule.params, 'minTenengrad', 60),
      noiseSigma: roundTo(quality.noise.sigma, 3),
      sampledAt: quality.sampledAt,
      displayValue: `${Math.round(roi.score * 100)} %`,
      estimate: true,
      ...(legacyFailEscalation ? { policyViolation: true } : {}),
    };

    return plainResult({
      id: sharpnessCheck.id,
      title: sharpnessCheck.title,
      status: verdict.status,
      severity: ctx.rule.severity,
      value: roundTo(roi.score, 4),
      min: thresholds.min,
      ...(Object.keys(official).length > 0 ? { official } : {}),
      ...(scorePolicy
        ? { policy: scorePolicy }
        : legacyFailEscalation
          ? { policy: bounds.policy as PolicySpec }
          : {}),
      measurement: {
        value: roundTo(roi.score, 4),
        unit: 'score',
        reference: 'roi_detail',
        tolerance: 0,
      },
      message,
      requirement: 'Изображение должно быть резким, без смаза и расфокуса.',
      fixability: 'retake',
      geometry,
      confidence: roundTo(confidence, 2),
      details,
    });
  },
};
